// Landing page targeting "Father's Day gifts" and "practical gifts for dad".
// Buyer is the son/daughter/spouse; the wedge is replacing dad's overstuffed
// bifold and the key bowl by the door with one trackable metal wallet.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";
import cashPhoto from "../assets/newest-version/Back - with cash.jpg";
import keysPhoto from "../assets/newest-version/Back - Keys Focus.jpg";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";

const futura = "'Futura', sans-serif";

const DAD_FAQ = [
  {
    q: "Why is The Brik a good Father's Day gift?",
    a: "Because it upgrades something dad uses every single day. The Brik replaces his overstuffed bifold with one metal wallet that holds his ID, 7-8 cards, folded cash, and his keys, with a rechargeable tracking card built in so a misplaced wallet takes two minutes to find instead of an afternoon.",
  },
  {
    q: "Will dad actually switch from his old leather wallet?",
    a: "The switch is easy: his ID goes in the front slot, his everyday cards go in the RFID-protected main compartment, cash folds under the back band, and his keys clip onto the included keyring. Most dads carry fewer things than they think; the Brik holds all of them.",
  },
  {
    q: "What if dad loses things constantly?",
    a: "That's exactly who this is for. The built-in tracking card lets him ring the wallet or see it on a map from his phone. And since his keys attach to the wallet, finding one means finding both.",
  },
  {
    q: "Does it work with his phone?",
    a: "Yes. Pick Apple or Android at checkout and the Brik ships with the matching tracking card. The battery lasts up to 6 months per charge and recharges on any wireless charger, so there's nothing for him to maintain.",
  },
  {
    q: "Will it arrive in time for Father's Day?",
    a: "Orders are processed in about 1 business day and arrive within 5-7 days. Order a week or more ahead and you're comfortably covered, with a 30-day exchange guarantee if anything arrives wrong.",
  },
];

const DAD_POINTS = [
  {
    img: keysPhoto,
    alt: "House and car keys attached to the Brik metal wallet, a practical Father's Day gift",
    title: "Empties the key bowl",
    text: "House key, car key, shed key: they clip onto the wallet itself. The bowl by the door retires, and so does the nightly 'where are my keys' lap around the house.",
  },
  {
    img: cashPhoto,
    alt: "Folded cash held on the back of the Brik wallet for dads who pay in cash",
    title: "Keeps his cash habit",
    text: "Dads pay cash. The elastic back band holds folded bills tight, so he keeps the habit without the brick of receipts his bifold turned into.",
  },
  {
    img: trackerPhoto,
    alt: "Tracking card inside the Brik wallet so dad can find it from his phone",
    title: "Ends the wallet hunt",
    text: "Other jacket, truck console, workbench: wherever the wallet went, the built-in tracking card rings it or puts it on a map from his phone.",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const FathersDayGifts = () => {
  const faqJsonLd = makeFaqJsonLd(DAD_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Father's Day Gifts", path: "/fathers-day-gifts" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Father's Day Gifts He'll Actually Use"
        description="Skip the tie. The Brik replaces dad's overstuffed bifold and the key bowl by the door with one metal wallet for his cards, cash, ID, and keys, trackable from his phone. $69.99."
        path="/fathers-day-gifts"
        keywords="fathers day gifts, practical gifts for dad, gifts for dad who has everything, gifts for dad who loses everything, wallet for dad, useful fathers day gift ideas, birthday gift for dad"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            FOR DAD
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            Father's Day Gifts He'll Actually Use
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            The best Father's Day gift upgrades something dad touches every
            day. The Brik replaces his overstuffed bifold and the key bowl by
            the door with one metal wallet for his cards, cash, ID, and keys,
            with a tracking card built in so he can find it from his phone.
            $69.99.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get Dad the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            In stock · arrives in 5-7 days · 30-day exchange guarantee
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Father's Day gift: The Brik metal wallet with cards, ID, cash, and keys attached"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* The bifold problem */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>Retire the fifteen-year-old bifold</h2>
          <p style={bodyText}>
            You know the wallet. Leather gone soft, shaped like a fist,
            holding two working cards, nine receipts, and a punch card from a
            sandwich shop that closed. Dad says he doesn't need anything new.
            Dad says that about everything.
          </p>
          <p style={bodyText}>
            The Brik is the upgrade he wouldn't buy himself: a solid block of
            machined anodized aluminum that holds his ID up front, 7-8 cards
            behind RFID protection, folded cash in the back, and his keys on
            an integrated ring. Nothing to break in, nothing to sag, and when
            it goes missing, his phone finds it.
          </p>
          <p style={{ ...bodyText, fontSize: "14px", color: "#666", margin: 0 }}>
            Also works for the other giftable moments:{" "}
            <a href="/graduation-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              graduation gifts
            </a>
            ,{" "}
            <a href="/groomsmen-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              groomsmen packs
            </a>
            , and{" "}
            <a
              href="/gifts-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              gifts for college kids
            </a>
            .
          </p>
        </div>
      </section>

      {/* Dad-mapped features */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "40px" }}>
            Built for how dads actually carry
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "28px",
            }}
          >
            {DAD_POINTS.map((f) => (
              <div key={f.title}>
                <div
                  style={{
                    background: "#989898",
                    borderRadius: "14px",
                    overflow: "hidden",
                    aspectRatio: "4 / 3",
                    marginBottom: "16px",
                  }}
                >
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    width={1024}
                    height={1024}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "19px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "15px", margin: 0 }}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Father's Day questions, answered
          </h2>
          {DAD_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            He taught you where everything goes.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            Give him the wallet that keeps it all in one place. $69.99, in
            stock, arrives in 5-7 days.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FathersDayGifts;
